import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Phone, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CCRSummary() {
  const [summaryData, setSummaryData] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [searchPosition, setSearchPosition] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailRecruiter, setDetailRecruiter] = useState('');
  const [detailDate, setDetailDate] = useState('');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `${API}/ccr/summary`;
      const params = [];
      if (filterDateFrom) params.push(`date_from=${filterDateFrom}`);
      if (filterDateTo) params.push(`date_to=${filterDateTo}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setSummaryData(res.data);
    } catch (e) { toast.error('Failed to fetch CCR summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, [filterDateFrom, filterDateTo]);

  const uniqueRecruiters = useMemo(() => [...new Set(summaryData.map(r => r.recruiter_name).filter(Boolean))].sort(), [summaryData]);

  const filteredData = useMemo(() => {
    let data = summaryData;
    if (filterRecruiter !== 'all') data = data.filter(r => r.recruiter_name === filterRecruiter);
    if (searchPosition) {
      const q = searchPosition.toLowerCase();
      data = data.filter(r => r.sessions?.some(s => (s.position_title || '').toLowerCase().includes(q)));
    }
    return data;
  }, [summaryData, filterRecruiter, searchPosition]);

  const totals = useMemo(() => {
    return filteredData.reduce((acc, row) => ({
      total_calls: acc.total_calls + (row.total_calls || 0),
      pre_lunch: acc.pre_lunch + (row.pre_lunch || 0),
      post_lunch: acc.post_lunch + (row.post_lunch || 0),
      prospects: acc.prospects + (row.prospects || 0),
      jd_sent: acc.jd_sent + (row.jd_sent || 0),
      sent_to_client: acc.sent_to_client + (row.sent_to_client || 0),
    }), { total_calls: 0, pre_lunch: 0, post_lunch: 0, prospects: 0, jd_sent: 0, sent_to_client: 0 });
  }, [filteredData]);

  const openDetail = async (recruiter_id, recruiter_name, date) => {
    setDetailRecruiter(recruiter_name);
    setDetailDate(date);
    try {
      const res = await axios.get(`${API}/ccr/detail/${recruiter_id}/${date}`, { headers: getHeaders() });
      setDetailData(res.data);
      setDetailOpen(true);
    } catch (e) { toast.error('Failed to fetch details'); }
  };

  const clearFilters = () => { setFilterDateFrom(''); setFilterDateTo(''); setFilterRecruiter('all'); setSearchPosition(''); };

  const StatusBadge = ({ status }) => {
    if (!status) return <span className="text-xs text-slate-300">-</span>;
    const colors = {
      'Not Picked': 'bg-slate-100 text-slate-700', 'Not Interested': 'bg-red-50 text-red-700',
      'Rejected': 'bg-red-100 text-red-800', 'Prospect': 'bg-amber-100 text-amber-800',
      'JD Sent': 'bg-blue-100 text-blue-800', 'Sent to Client': 'bg-emerald-100 text-emerald-800',
    };
    return <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${colors[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="ccr-summary-title" style={{ fontFamily: 'Manrope, sans-serif' }}>CCR Summary</h1>
          <p className="text-slate-600">Daily calling effort summary across recruiters</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search Position</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-ccr-position" placeholder="Search by position..." value={searchPosition} onChange={(e) => setSearchPosition(e.target.value)} className="h-9 pl-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <select data-testid="filter-ccr-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Recruiters</option>
            {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
          <Input data-testid="filter-ccr-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-44" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
          <Input data-testid="filter-ccr-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-44" />
        </div>
        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs">Clear Filters</Button>
        <span className="text-xs text-slate-500 ml-auto">{filteredData.length} records</span>
      </div>

      {loading ? (
        <p className="text-slate-500 py-8 text-center">Loading...</p>
      ) : filteredData.length === 0 ? (
        <div className="bg-white border border-blue-100 rounded shadow-sm p-12 text-center">
          <Phone size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No calling reports found for the selected filters</p>
        </div>
      ) : (
        <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
                <TableHead className="font-bold text-blue-900">Date</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Total Calls</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Pre Lunch</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Post Lunch</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Prospects</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">JD Sent</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Sent to Client</TableHead>
                <TableHead className="font-bold text-blue-900">Positions</TableHead>
                <TableHead className="font-bold text-blue-900">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow key={`${row.recruiter_id}-${row.date}`} data-testid={`ccr-summary-row-${idx}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900">{row.recruiter_name}</TableCell>
                  <TableCell className="text-sm">{row.date}</TableCell>
                  <TableCell className="text-center font-bold text-blue-800">{row.total_calls}</TableCell>
                  <TableCell className="text-center"><span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded">{row.pre_lunch}</span></TableCell>
                  <TableCell className="text-center"><span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-2 py-0.5 rounded">{row.post_lunch}</span></TableCell>
                  <TableCell className="text-center font-semibold text-amber-700">{row.prospects}</TableCell>
                  <TableCell className="text-center font-semibold text-blue-700">{row.jd_sent}</TableCell>
                  <TableCell className="text-center font-semibold text-emerald-700">{row.sent_to_client}</TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {row.sessions?.map((s) => (
                      <span key={s.position_title} className="block">{s.position_title} ({s.entries_count})</span>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" data-testid={`view-detail-${idx}`} onClick={() => openDetail(row.recruiter_id, row.recruiter_name, row.date)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-bold">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {/* Summary Row */}
              <TableRow className="bg-blue-100 border-t-2 border-blue-300" data-testid="ccr-summary-totals">
                <TableCell className="font-extrabold text-blue-900" colSpan={2}>TOTAL</TableCell>
                <TableCell className="text-center font-extrabold text-blue-900">{totals.total_calls}</TableCell>
                <TableCell className="text-center font-extrabold text-amber-800">{totals.pre_lunch}</TableCell>
                <TableCell className="text-center font-extrabold text-indigo-800">{totals.post_lunch}</TableCell>
                <TableCell className="text-center font-extrabold text-amber-700">{totals.prospects}</TableCell>
                <TableCell className="text-center font-extrabold text-blue-700">{totals.jd_sent}</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-700">{totals.sent_to_client}</TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-5xl rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {detailRecruiter} — {detailDate}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[65vh] space-y-6">
            {detailData.map((session, si) => (
              <div key={`${session.position_id || session.position_title}-${si}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">{session.position_title}</span>
                  <span className="text-xs text-slate-500">{session.client_name} — {session.entries?.length || 0} entries</span>
                </div>
                {session.entries?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold">#</TableHead>
                        <TableHead className="text-xs font-bold">Name</TableHead>
                        <TableHead className="text-xs font-bold">Mobile</TableHead>
                        <TableHead className="text-xs font-bold">Session</TableHead>
                        <TableHead className="text-xs font-bold">1st Call</TableHead>
                        <TableHead className="text-xs font-bold">2nd Call</TableHead>
                        <TableHead className="text-xs font-bold">3rd Call</TableHead>
                        <TableHead className="text-xs font-bold">Remarks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {session.entries.map((e, ei) => (
                        <TableRow key={e.id || e.candidate_name + '-' + ei} className="hover:bg-blue-50">
                          <TableCell className="text-xs">{ei + 1}</TableCell>
                          <TableCell className="text-sm font-medium">{e.candidate_name}</TableCell>
                          <TableCell className="text-sm">{e.mobile_no}</TableCell>
                          <TableCell>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${e.session === 'pre_lunch' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'}`}>
                              {e.session === 'pre_lunch' ? 'Pre' : 'Post'}
                            </span>
                          </TableCell>
                          <TableCell><StatusBadge status={e.status_1st_call} /></TableCell>
                          <TableCell><StatusBadge status={e.status_2nd_call} /></TableCell>
                          <TableCell><StatusBadge status={e.status_3rd_call} /></TableCell>
                          <TableCell className="text-xs text-slate-600 max-w-[150px] truncate">{e.remarks}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-slate-400 py-2">No entries</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
